import dayjs from "dayjs";
import { captureBlocks } from "./captureBlocks";
import { planPdfPages } from "./planPdfPages";
import { renderPdf } from "./renderPdf";
import { loadDataTramLogo } from "./loadDataTramLogo";

export class PdfExportError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message);
    this.name = "PdfExportError";
    if (options?.cause !== undefined) {
      this.cause = options.cause;
    }
  }
}

export interface ExportDashboardAsPdfParams {
  containerEl: HTMLElement;
  dashboardName: string;
  dateRange: { startDate: string; endDate: string };
}

function slugifyDashboardName(name: string): string {
  return name.trim().replace(/\s+/g, "-");
}

function formatDateForFilename(isoDate: string): string {
  const parsed = dayjs(isoDate);
  return parsed.isValid() ? parsed.format("DD-MMM-YYYY") : isoDate;
}

function buildFilename(dashboardName: string, dateRange: { startDate: string; endDate: string }): string {
  const namePart = slugifyDashboardName(dashboardName);
  const startPart = formatDateForFilename(dateRange.startDate);
  const endPart = formatDateForFilename(dateRange.endDate);
  return `DataTram_${namePart}_${startPart}_${endPart}.pdf`;
}

export async function exportDashboardAsPdf(params: ExportDashboardAsPdfParams): Promise<void> {
  const { containerEl, dashboardName, dateRange } = params;

  try {
    const capturedRows = await captureBlocks(containerEl);
    const capturedBlocks = capturedRows.flatMap(row => row.blocks);
    if (capturedBlocks.length === 0) {
      throw new Error("No exportable content found in dashboard container");
    }

    const layout = planPdfPages(
      capturedRows.map(row => ({
        id: row.id,
        blocks: row.blocks.map(block => ({
          id: block.id,
          widthMm: block.widthMm,
          heightMm: block.heightMm,
          splittable: block.splittable,
          continueOnPage: block.continueOnPage,
        })),
      })),
    );

    const dateRangeLabel = `${formatDateForFilename(dateRange.startDate)} - ${formatDateForFilename(dateRange.endDate)}`;
    const logo = await loadDataTramLogo();
    const pdf = renderPdf(layout, capturedBlocks, {
      dashboardName,
      dateRangeLabel,
      generatedOnLabel: `Generated: ${dayjs().format("DD-MMM-YYYY")}`,
      logo,
    });

    pdf.save(buildFilename(dashboardName, dateRange));
  } catch (error) {
    throw new PdfExportError("Failed to generate dashboard PDF", { cause: error });
  }
}
